import { Column } from '@tanstack/react-table'
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ChevronsUpDown,
  Filter,
  Search,
  X,
  XIcon,
} from 'lucide-react'

import { Button } from '@repo/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/select'
import { Cfl, cn } from '@repo/ui/utils'
import { HTMLAttributes } from 'react'

interface DataTableColumnHeaderProps<TData, TValue>
  extends HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  options?: {
    filterable?: string[]
    searchable?: boolean
    sortable?: boolean
  }
  title?: string
}

export function DataTableColumnHeader<TData, TValue>({
  className,
  column,
  options,
  title,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const { tc } = useT()

  if (!column.getCanSort()) {
    return <div className={cn('select-none', className)}>{title}</div>
  }

  return (
    <div className={cn('flex items-center space-x-1', className)}>
      {options?.filterable ? (
        <>
          <Select
            onValueChange={(v) => {
              if (v === 'no filter') {
                column.setFilterValue(undefined)
              } else {
                column.setFilterValue(v)
              }
            }}
            defaultValue={undefined}
          >
            <SelectTrigger
              isChevronVisible={false}
              className="max-w-fit border-none bg-transparent p-0 ring-0 ring-transparent ring-offset-transparent focus:ring-transparent focus-visible:border-transparent focus-visible:ring-0 focus-visible:ring-offset-transparent"
            >
              {column.getIsFiltered() ? (
                <SelectValue />
              ) : (
                <div className="flex items-center">
                  <Filter className="mr-1 h-3.5 w-3.5" />
                  {Cfl(column.id)}
                </div>
              )}
            </SelectTrigger>
            <SelectContent>
              {[column.getIsFiltered() && 'no filter', ...options.filterable]
                .filter((v) => typeof v === 'string')
                .map((v, i) => (
                  <SelectItem value={v} key={i}>
                    <div className="flex items-center">
                      {column.getFilterValue() === v && (
                        <Filter className="text-highlight mr-1 h-3.5 w-3.5" />
                      )}
                      {v === 'no filter' && (
                        <XIcon className="mr-1 h-3.5 w-3.5" />
                      )}
                      {Cfl(v)}
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </>
      ) : (
        <span className="select-none">
          {title ?? tc(column.id, { cfl: true })}
        </span>
      )}

      {options && (
        <div className="flex items-center">
          {options.sortable && !column.getIsFiltered() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="data-[state=open]:bg-accent h-6 w-6"
                  tabIndex={-1}
                >
                  {column.getIsSorted() === 'desc' ? (
                    <ArrowUpAZ className="h-3.5 w-3.5" />
                  ) : column.getIsSorted() === 'asc' ? (
                    <ArrowDownAZ className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronsUpDown className="h-3.5 w-3.5" />
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
                  <ArrowDownAZ className="text-muted-foreground/70 h-3.5 w-3.5" />
                  Asc
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
                  <ArrowUpAZ className="text-muted-foreground/70 h-3.5 w-3.5" />
                  Desc
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {options.searchable && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={column.getIsFiltered() ? 'highlight' : 'ghost'}
                  size="icon"
                  className={cn(
                    'h-6 w-6',
                    !column.getIsFiltered() && 'data-[state=open]:bg-accent',
                  )}
                  onClick={() => {
                    if (column.getIsFiltered()) column.setFilterValue(undefined)
                  }}
                  tabIndex={-1}
                >
                  {column.getIsFiltered() ? (
                    <X className="h-3.5 w-3.5" />
                  ) : (
                    <Search className="h-3.5 w-3.5" />
                  )}
                </Button>
              </PopoverTrigger>

              <PopoverContent className="w-44">
                <Input
                  type="text"
                  placeholder="Filter results"
                  className=""
                  value={(column.getFilterValue() as string) ?? ''}
                  onChange={(e) => column.setFilterValue(e.target.value)}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </div>
  )
}
